import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Row, FormControl, Button } from 'react-bootstrap';
import Select from 'react-select';
import _ from 'lodash';
import moment from 'moment';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';
import withForm from '../../hoc/withForm';

import { useAlertStore } from '../../store/providers/Alert';
import { useMlsProviderStore } from '../../store/providers/MlsProviders';
import { useBrokerMetricsStore } from '../../store/providers/BrokerMetrics';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import Spinner from '../../components/Spinner/Spinner';
import Permission from '../../components/Permission/Permission';

import validations from './validations';
import './brokerMetrics.css';

import * as constants from '../../constants';

const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const InputCollection = React.lazy(() => import('../../components/InputCollection/InputCollection'));
const InformationModal = React.lazy(() => import('../../components/Modals/InformationModal/InformationModal'));

function BrokerMetrics(props) {
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { listRecords, clearData, data } = useMlsProviderStore();
    const { listRecords: generateMetrics, data: brokerMetricsData } = useBrokerMetricsStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [isMetricsLoaded, setIsMetricsLoaded] = useState(false);
    const [mlsProviderOptions, setMlsProviderOptions] = useState([]);
    const [showInformationModal, setShowInformationModal] = useState(false);

    const pagePermission = constants.PERMISSION_REPORTS_BROKERMETRICS;
    const [hasPermission, setHasPermission] = useState(false);
    const [, setCanEdit] = useState(false);

    const submittedHandler = (e) => {
        e.preventDefault();

        setIsMetricsLoaded(false);

        var valid = props.formValidate(validations);

        if (valid) {
            props.formSubmitted();
            generateMetrics({ feedId: props.formData.mlsProviderId, ...props.formData });
        }
    };

    const infoClickedHandler = () => {
        setShowInformationModal(false);
    };

    useEffect(() => {
        if (!_.isEmpty(data)) {            
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (!isLoaded && data.status === 'success' && data.scope === 'list')
            {
                const startDate = moment().startOf('quarter').subtract(1, 'ms').startOf('quarter');
                const endDate = moment(startDate).endOf('quarter');

                props.formInitialize({
                    mlsProviderId: '',
                    startDate: startDate.format('YYYY-MM-DD'),
                    endDate: endDate.format('YYYY-MM-DD')
                });

                setIsLoaded(true);
            }
        }

        if (!_.isEmpty(brokerMetricsData))
        {
            if (!isMetricsLoaded && brokerMetricsData.status === 'success' && brokerMetricsData.scope === 'list')
            {
                setIsMetricsLoaded(true);
                setShowInformationModal(true);
            }
        }

        setIsBusy((!_.isEmpty(data) && data.status === 'processing' && data.scope === 'list') || (!_.isEmpty(brokerMetricsData) && brokerMetricsData.status === 'processing' && brokerMetricsData.scope === 'list'));
    }, [data, addAlert, clearData, props, isLoaded, brokerMetricsData, isMetricsLoaded]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        listRecords({ allowInactive: false });

        dispatch(breadcrumbAddRoute({ key: 'brokerMetrics', text: 'Broker Metrics', path: window.location.pathname }));

        setIsMounted(true);
    }, [isMounted, hasPermission, dispatch, listRecords]);

    useEffect(() => {        
        if (!_.isEmpty(data) && data.scope === 'list' && data.status === 'success') {
            const providerList = data.body.map((item) => {
                return { value: item.redName, label: `${item.name} (${item.redName})` };
            });

            setMlsProviderOptions(providerList);
        }
    }, [data]);

    return (
        <>
            <SideMenu activeKey="reports" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit}/>
                { hasPermission && isMounted &&
                    <>
                        {
                            showInformationModal && <InformationModal
                                show={showInformationModal}
                                clicked={infoClickedHandler}
                                title="Broker Metrics Information"
                                buttonText='OK! Got it!'>
                                <div>
                                    Broker Metrics take a little time to run.  Your request is currently being processed and a link containing the metrics data
                                    will be emailed to you within the next few minutes.
                                </div>
                            </InformationModal>
                        }

                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Broker Metrics" />
                            {
                                !isLoaded && <Spinner text="Loading Broker Metrics..." />
                            }
                            {
                                isLoaded && (
                                    <Form onSubmit={(e) => submittedHandler(e)}>
                                        <Row>
                                            <div className="col-md-4">
                                                <InputCollection required labelText="Select MLS Provider" errorMessage={props.formValidationCheck('mlsProviderId')}>
                                                    <Select id="mlsProviderId" isDisabled={isBusy} onChange={(e) => props.formChanged('mlsProviderId', e.value)} isLoading={isBusy} isClearable={false} isSearchable={true} options={mlsProviderOptions} />
                                                </InputCollection>
                                            </div>
                                            <div className="col-md-4">
                                                <InputCollection labelText="Start Date" errorMessage={props.formValidationCheck('startDate')}>
                                                    <FormControl id="startDate" type="date" onChange={(e) => props.formChanged(e.target.id, e.target.value)} autoComplete="off" maxLength={10} defaultValue={props.formData['startDate'] || ''} disabled={isBusy} />
                                                </InputCollection>
                                            </div>
                                            <div className="col-md-4">
                                                <InputCollection labelText="End Date" errorMessage={props.formValidationCheck('endDate')}>
                                                    <FormControl id="endDate" type="date" onChange={(e) => props.formChanged(e.target.id, e.target.value)} autoComplete="off" maxLength={10} defaultValue={props.formData['endDate'] || ''} disabled={isBusy} />
                                                </InputCollection>
                                            </div>
                                        </Row>
                                        <hr className="form-divider" />
                                        <div className="form-action-buttons">
                                            <Button bsStyle="danger" variant="danger" type="submit" className="button-group-margin" disabled={isBusy}>
                                                <i className="fa fa-play button-icon"></i>
                                                { isBusy ? 'Busy...' : 'Run' }
                                            </Button>
                                        </div>
                                    </Form>
                                )
                            }
                        </div>
                    </>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(withForm(BrokerMetrics)));